/**
 * S06 — AI Opportunity Radar.
 *
 * The Identify pillar of the ALGOS flywheel: ranked AI opportunities by
 * ROI × Risk × Effort, with three views (matrix · table · card), filter +
 * sort + saved views, and the gateway to S07 (detail drawer) and S08
 * (submit request).
 *
 * Demo Mode: pre-populated demo tenant data + any session overlays.
 * Customer Mode: professional empty state + onboarding nudges.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Plus,
  Compass,
  Database,
  ListChecks,
  Layers,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { can, readOnlyReason } from "@/lib/rbac";
import { Flywheel } from "@/components/brand/Flywheel";
import { fmtAud, KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { useOpportunityRadar } from "./useOpportunityRadar";
import { RadarToolbar } from "./components/RadarToolbar";
import { OpportunityMatrix } from "./components/OpportunityMatrix";
import { OpportunityTable } from "./components/OpportunityTable";
import { OpportunityCardGrid } from "./components/OpportunityCardGrid";
import OpportunityDetailDrawer from "./OpportunityDetailDrawer";
import SubmitOpportunityRequest from "./SubmitOpportunityRequest";

export default function OpportunityRadar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isCustomer, tenant, pendingEntities, promoteOpportunity } = useTenantData();
  // Phase 3b — fetch the canonical list from the REST endpoint and
  // overlay any opportunities the user just submitted in this session
  // (the overlay items are deduped by id so a later refetch won't
  // produce phantom duplicates).
  const { items: serverOpps, loading, error, lastSyncedAt, refetch } = useEntityList("/api/opportunities");
  const opportunities = useMemo(() => {
    const pending = pendingEntities?.opportunities || [];
    if (pending.length === 0) return serverOpps;
    const pendingIds = new Set(pending.map((o) => o.id));
    return [...pending, ...serverOpps.filter((o) => !pendingIds.has(o.id))];
  }, [serverOpps, pendingEntities]);
  const radar = useOpportunityRadar(opportunities);

  const [drawerOpp, setDrawerOpp] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitPrefill, setSubmitPrefill] = useState(null);

  const handleSelect = useCallback((o) => setDrawerOpp(o), []);
  const handleSubmitFromDrawer = useCallback((o) => {
    setSubmitPrefill(o);
    setSubmitOpen(true);
    setDrawerOpp(null);
  }, []);
  const handlePromoteFromDrawer = useCallback(async (o) => {
    if (!promoteOpportunity) return;
    const t = toast.loading(`Promoting ${o.id} to deployment workflow…`);
    try {
      const { request } = await promoteOpportunity(o.id);
      toast.success(`Created ${request.id} · stage 1`, { id: t, description: "Walk it through the stage-gate to mint a live agent." });
      setDrawerOpp(null);
      navigate(`/deploy/requests?focus=${request.id}`);
    } catch (e) {
      toast.error("Promote failed", { id: t, description: e?.message || "Unknown error" });
    }
  }, [promoteOpportunity, navigate]);
  const handleSubmitNew = useCallback(() => {
    setSubmitPrefill(null);
    setSubmitOpen(true);
  }, []);
  const handleSubmitClose = useCallback(() => {
    setSubmitOpen(false);
    setSubmitPrefill(null);
    // The overlay already carries the new opp for instant UI; refetch
    // brings the canonical server list in sync for the dedupe pass.
    refetch();
  }, [refetch]);

  if (loading && opportunities.length === 0) {
    return <RadarSkeleton />;
  }
  if (error) {
    return <RadarError message={error.message} />;
  }
  if (isCustomer && opportunities.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSubmitNew={handleSubmitNew} />;
  }

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="opportunity-radar"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header
        tenantName={tenant.name}
        onSubmitNew={handleSubmitNew}
        isCustomer={isCustomer}
        canSubmit={can(user?.id, "submit:opportunity")}
        denialReason={readOnlyReason(user?.id)}
        lastSyncedAt={lastSyncedAt}
        syncing={loading}
        onRefetch={refetch}
      />

      <KpiStrip opportunities={opportunities} />

      <div className="mt-4">
        <RadarToolbar {...radar} />
      </div>

      <div className="mt-4">
        {radar.view === "matrix" && (
          <OpportunityMatrix items={radar.items} onSelect={handleSelect} />
        )}
        {radar.view === "table" && (
          <OpportunityTable items={radar.items} onSelect={handleSelect} />
        )}
        {radar.view === "card" && (
          <OpportunityCardGrid items={radar.items} onSelect={handleSelect} />
        )}
      </div>

      {/* S07 Detail drawer */}
      <OpportunityDetailDrawer
        opportunity={drawerOpp}
        onClose={() => setDrawerOpp(null)}
        onSubmitRequest={handleSubmitFromDrawer}
        onPromote={handlePromoteFromDrawer}
      />

      {/* S08 Submit modal */}
      <SubmitOpportunityRequest
        open={submitOpen}
        onClose={handleSubmitClose}
        prefill={submitPrefill}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Header({ tenantName, onSubmitNew, canSubmit = true, denialReason, lastSyncedAt, syncing, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Identify · Opportunity Radar</p>
        <h1 className="text-display-md text-ink-primary mt-1">AI opportunity radar</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          Ranked AI use-cases for {tenantName}, scored on ROI, risk and effort. Open any
          opportunity to see the full business case, or submit a new one to enter governance.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={syncing}
          onRefetch={onRefetch}
          testId="opportunity-radar-synced"
        />
        <button
          type="button"
          onClick={canSubmit ? onSubmitNew : undefined}
          disabled={!canSubmit}
          className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold transition-colors shadow-e1 ${
            canSubmit
              ? "bg-tralenex-600 text-white hover:bg-tralenex-700"
              : "bg-inset text-ink-disabled cursor-not-allowed border border-border-subtle"
          }`}
          data-testid="radar-submit-new"
          title={canSubmit ? "Submit a new AI opportunity" : denialReason}
          aria-disabled={!canSubmit}
        >
          <Plus size={14} /> Submit opportunity
        </button>
      </div>
    </div>
  );
}

function KpiStrip({ opportunities }) {
  const total = opportunities.length;
  const totalValue = opportunities.reduce((s, o) => s + (o.estimatedValueAud || 0), 0);
  const avgPriority = total
    ? Math.round(opportunities.reduce((s, o) => s + (o.priorityScore || 0), 0) / total)
    : 0;
  const endorsed = opportunities.filter((o) => o.status === "endorsed").length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      <KpiTile
        label="Opportunities"
        value={total.toString()}
        delta={`${endorsed} endorsed`}
        deltaTone="up"
        hint="on the radar"
        icon={Compass}
        tone="opp"
        testId="radar-kpi-total"
      />
      <KpiTile
        label="Aggregate annual value"
        value={fmtAud(totalValue)}
        delta="+12% QoQ"
        deltaTone="up"
        hint="across all opportunities"
        icon={Database}
        tone="low"
        testId="radar-kpi-value"
      />
      <KpiTile
        label="Avg. priority score"
        value={`${avgPriority}`}
        delta={avgPriority >= 60 ? "Strong pipeline" : "Build pipeline"}
        deltaTone={avgPriority >= 60 ? "up" : "neutral"}
        hint="ROI · 100 − Risk · 100 − Effort"
        icon={ListChecks}
        tone="tralenex"
        testId="radar-kpi-priority"
      />
      <KpiTile
        label="Ready for governance"
        value={`${endorsed}`}
        delta="Awaiting submission"
        deltaTone="neutral"
        hint="endorsed by sponsor"
        icon={Layers}
        tone="gold"
        testId="radar-kpi-ready"
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Customer-Mode empty state
// ───────────────────────────────────────────────────────────────────────────
function CustomerEmpty({ userName, onSubmitNew }) {
  const navigate = useNavigate();
  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up"
      data-testid="radar-customer-empty"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>

      <div className="relative max-w-3xl">
        <p className="eyebrow">Identify · Opportunity Radar</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          Your radar is empty — that is the right starting point. Capture your first AI
          opportunity to begin. ALGOS will rank it by ROI, risk and effort, and route it
          through the governance pipeline.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <EmptyAction
            number="01"
            title="Submit your first AI opportunity"
            description="Use the guided submission to capture title, business problem, proposed solution and value."
            icon={Plus}
            onClick={onSubmitNew}
            testId="radar-empty-submit"
            primary
          />
          <EmptyAction
            number="02"
            title="Preview ALGOS with mature data"
            description="Switch to Demo Mode to see the demo tenant's full opportunity pipeline."
            icon={Sparkles}
            onClick={() => navigate("/dashboard")}
            testId="radar-empty-demo"
          />
        </div>

        <p className="mt-8 text-meta text-ink-secondary">
          Process mining, signals from BU heads, or board priorities — all of these
          start as opportunities on this radar.
        </p>
      </div>
    </div>
  );
}

function EmptyAction({ number, title, description, icon: Icon, onClick, testId, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "group surface-card p-5 text-left transition-all hover:shadow-e2 " +
        (primary
          ? "border-tralenex-300 bg-tralenex-50/30 hover:border-tralenex-600"
          : "hover:border-tralenex-300")
      }
    >
      <div className="flex items-start justify-between">
        <span className="text-meta font-mono text-ink-disabled tabular-nums">{number}</span>
        <Icon
          size={16}
          className={primary ? "text-tralenex-700" : "text-ink-secondary group-hover:text-tralenex-700"}
        />
      </div>
      <p className="mt-3 text-h3 text-ink-primary">{title}</p>
      <p className="mt-1 text-meta text-ink-secondary">{description}</p>
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Phase 3b — load + error states for REST-fetched radar.
// ───────────────────────────────────────────────────────────────────────────
function RadarSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="opportunity-radar-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[440px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function RadarError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="opportunity-radar-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Identify · Opportunity Radar</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load opportunities</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

